import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Sparkles, Zap, Crown, Gift, Copy, Users, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { subscriptionApi } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PricingModal = ({ isOpen, onClose, currentPlan, onUpgradeSuccess, usage }) => {
  const [loading, setLoading] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [userCountry, setUserCountry] = useState('US');
  const [priceLoading, setPriceLoading] = useState(true);

useEffect(() => {
  const cached = localStorage.getItem('simplify_country');
  const cachedAt = localStorage.getItem('simplify_country_at');
  const oneDay = 24 * 60 * 60 * 1000;

  if (cached && cachedAt && Date.now() - parseInt(cachedAt) < oneDay) {
    setUserCountry(cached);
    setPriceLoading(false);
    return;
  }

  fetch(`${API_URL}/api/geo/country`)
    .then(res => res.json())
    .then(data => {
      localStorage.setItem('simplify_country', data.country);
      localStorage.setItem('simplify_country_at', Date.now().toString());
      setUserCountry(data.country);
      setPriceLoading(false);
    })
    .catch(() => {
      setUserCountry('US');
      setPriceLoading(false);
    });
}, []);

  useEffect(() => {
    if (isOpen) {
      fetchReferralStats();
    }
  }, [isOpen]);

  const fetchReferralStats = async () => {
    try {
      const token = localStorage.getItem('simplify_token');
      const response = await axios.get(`${API_URL}/api/referral/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralStats(response.data);
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
    }
  };

  const copyReferralLink = async () => {
    if (!referralStats?.referral_link) return;
    try {
      await navigator.clipboard.writeText(referralStats.referral_link);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const isIndia = userCountry === 'IN';
  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      displayPrice: '0',
      period: 'forever',
      papers: 5,
      icon: Sparkles,
      features: [
        '5 paper analyses per month',
        'All analysis features',
        'PDF storage',
        'Basic support'
      ],
      buttonText: 'Current Plan',
      disabled: true
    },
    {
      id: 'basic',
      name: 'Basic',
      price: 4.99,
      displayPrice: isIndia ? '₹249' : '$4.99',
      period: 'month',
      papers: 50,
      icon: Zap,
      popular: true,
      features: [
        '50 paper analyses per month',
        'All analysis features',
        'PDF storage',
        'Priority processing',
        'Email support'
      ],
      buttonText: 'Upgrade to Basic'
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 9.99,
      displayPrice: isIndia ? '₹649' : '$9.99',
      period: 'month',
      papers: -1,
      icon: Crown,
      features: [
        'Unlimited paper analyses',
        'All analysis features',
        'PDF storage',
        'Priority processing',
        'Priority support',
        'Early access to new features'
      ],
      buttonText: 'Upgrade to Pro'
    }
  ];

  const handleUpgrade = async (planId) => {
    if (planId === 'free' || planId === currentPlan) return;
    
    setLoading(planId);
    try {
      const token = localStorage.getItem('simplify_token');
      const response = await axios.post(
        `${API_URL}/api/subscription/checkout`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await subscriptionApi.cancelSubscription();
      toast.success(response.data.message);
      setShowCancelConfirm(false);
      onUpgradeSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-4xl bg-slate-900 rounded-3xl border border-slate-700/50 shadow-2xl overflow-y-auto max-h-[90vh]"
        >
          {/* Header */}
          <div className="p-6 border-b border-slate-700/50 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-heading font-bold text-white">Choose Your Plan</h2>
              <p className="text-slate-400 mt-1">Unlock more paper analyses with our premium plans</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
              data-testid="close-pricing-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Plans */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((plan) => {
                const Icon = plan.icon;
                const isCurrent = plan.id === currentPlan;
                const isDisabled = plan.id === 'free' || isCurrent;
                
                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`
                      relative p-6 rounded-2xl border transition-all
                      ${plan.popular 
                        ? 'bg-gradient-to-b from-teal-500/10 to-transparent border-teal-500/30' 
                        : 'bg-slate-800/30 border-slate-700/50'
                      }
                      ${isCurrent ? 'ring-2 ring-teal-400' : ''}
                    `}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="px-3 py-1 text-xs font-semibold bg-teal-500 text-slate-900 rounded-full">
                          Most Popular
                        </span>
                      </div>
                    )}

                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-2 rounded-xl ${plan.popular ? 'bg-teal-500/20' : 'bg-slate-700/50'}`}>
                        <Icon className={`w-5 h-5 ${plan.popular ? 'text-teal-400' : 'text-slate-400'}`} />
                      </div>
                      <h3 className="text-lg font-heading font-semibold text-white">{plan.name}</h3>
                    </div>

                    <div className="mb-4">
                      {priceLoading ? (
                        <span className="text-3xl font-heading font-bold text-slate-500">...</span>
                      ) : (
                        <span className="text-3xl font-heading font-bold text-white">
                          {plan.displayPrice}
                        </span>
                      )}
                      <span className="text-slate-400">/{plan.period}</span>
                    </div>

                    <p className="text-sm text-slate-400 mb-4">
                      {plan.papers === -1 ? 'Unlimited' : plan.papers} papers/month
                    </p>

                    <ul className="space-y-2 mb-6">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <Check className={`w-4 h-4 mt-0.5 shrink-0 ${plan.popular ? 'text-teal-400' : 'text-slate-500'}`} />
                          <span className="text-slate-300">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={isDisabled || loading === plan.id}
                      data-testid={`select-plan-${plan.id}`}
                      className={`
                        w-full
                        ${plan.popular 
                          ? 'bg-teal-500 hover:bg-teal-400 text-slate-900' 
                          : 'bg-slate-700 hover:bg-slate-600 text-white'
                        }
                        ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                    >
                      {loading === plan.id ? (
                        <div className="w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                      ) : isCurrent ? (
                        'Current Plan'
                      ) : (
                        plan.buttonText
                      )}
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Footer with Cancel and Referral */}
          <div className="p-6 bg-slate-800/30 border-t border-slate-700/50 space-y-4">
            {/* Cancel Subscription - Only for paid plans */}
            {currentPlan !== 'free' && !usage?.is_cancelled && (
              <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                {showCancelConfirm ? (
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-white">Cancel Subscription?</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Your subscription will remain active until {usage?.subscription_end_date ? new Date(usage.subscription_end_date).toLocaleDateString() : 'the end of your billing period'}. 
                          After that, you'll be moved to the Free plan.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowCancelConfirm(false)}
                        className="text-slate-400 hover:text-white"
                      >
                        Keep Plan
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleCancelSubscription}
                        disabled={cancelling}
                        data-testid="confirm-cancel-subscription"
                        className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                      >
                        {cancelling ? (
                          <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                        ) : (
                          'Yes, Cancel'
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-white">Manage Subscription</p>
                      <p className="text-xs text-slate-400">
                        Currently on {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} plan
                        {usage?.subscription_end_date && ` • Renews ${new Date(usage.subscription_end_date).toLocaleDateString()}`}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setShowCancelConfirm(true)}
                      data-testid="cancel-subscription-button"
                      className="bg-slate-700 hover:bg-slate-600 text-slate-300"
                    >
                      Cancel Plan
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Show cancelled status */}
            {usage?.is_cancelled && (
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-400">Subscription Cancelled</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Your benefits will remain active until {usage?.subscription_end_date ? new Date(usage.subscription_end_date).toLocaleDateString() : 'the end of your billing period'}.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Referral Section */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-teal-500/20">
                  <Gift className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Invite Friends, Get Free Papers</p>
                  <p className="text-xs text-slate-400">
                    Both you and your friend get 3 bonus analyses
                    {referralStats && (
                      <span className="ml-2">
                        • <Users className="w-3 h-3 inline" /> {referralStats.people_referred} invited
                        • +{referralStats.referral_credits} bonus papers
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Button
                onClick={copyReferralLink}
                size="sm"
                data-testid="copy-referral-modal"
                className="bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 border border-teal-500/30"
              >
                {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
            </div>
            <p className="text-xs text-slate-500 text-center mt-4">
              Secure payment powered by Stripe. Cancel anytime.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PricingModal;
